/**
 * Payment Modal Component - With Partial Payment Support - Using new Modal component
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { CurrencyInput } from '../../components/form/MaskedInputs';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { CreditCard, X, Check, Banknote, QrCode, Wallet, Clock, User, CheckCircle } from 'lucide-react';

const iconMap = { Banknote, CreditCard, QrCode, Wallet };

export function PaymentModal({
  open,
  onOpenChange,
  total,
  paidAmount,
  remaining,
  payments,
  paymentMethods,
  formatCurrency,
  onAddPayment,
  onUpdatePayment,
  onRemovePayment,
  onFinalize,
  onPartialPayment,
  onCloseAndContinue,
  previousPartialPaid = 0,
  partialPayments = [],
}) {
  const { t } = useTranslation();
  const [showPartialForm, setShowPartialForm] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [payerName, setPayerName] = useState('');

  // Total already paid (previous partial + current)
  const totalPaidSoFar = previousPartialPaid + paidAmount;
  const realRemaining = Math.max(0, total - totalPaidSoFar);

  const handlePartialPayment = () => {
    const amount = parseFloat(partialAmount);
    if (!amount || amount <= 0) {
      return;
    }
    
    if (onPartialPayment) {
      onPartialPayment({
        amount,
        payer_name: payerName || 'Cliente',
        payments: [...payments],
      });
    }
    
    setShowPartialForm(false);
    setPartialAmount('');
    setPayerName('');
  };

  const handleCloseAndContinue = () => {
    setShowPartialForm(false);
    setPartialAmount('');
    setPayerName('');
    if (onCloseAndContinue) {
      onCloseAndContinue();
    }
    onOpenChange(false);
  };

  const canFinalize = totalPaidSoFar >= total;
  const canPartialPay = paidAmount > 0 && totalPaidSoFar < total;

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t('pdv.payment')}
          </ModalTitle>
        </ModalHeader>
        
        <ModalBody className="space-y-4">
          {/* Previous Partial Payments */}
          {previousPartialPaid > 0 && partialPayments.length > 0 && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-500">Pagamentos Anteriores</span>
              </div>
              <div className="space-y-1">
                {partialPayments.map((pp, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{pp.payer_name || 'Cliente'}</span>
                    <span className="font-mono text-emerald-500">{formatCurrency(pp.amount)}</span>
                  </div>
                ))}
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between text-sm font-medium">
                <span>Total já pago</span>
                <span className="font-mono text-emerald-500">{formatCurrency(previousPartialPaid)}</span>
              </div>
            </div>
          )}

          {/* Totals Summary */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-between text-lg font-bold">
              <span>{t('pdv.total')}</span>
              <span className="font-mono text-primary">{formatCurrency(total)}</span>
            </div>
            {previousPartialPaid > 0 && (
              <div className="flex justify-between text-sm mt-2">
                <span className="text-muted-foreground">Já pago (parcial)</span>
                <span className="font-mono text-emerald-500">{formatCurrency(previousPartialPaid)}</span>
              </div>
            )}
            {paidAmount > 0 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Pagando agora</span>
                <span className="font-mono text-emerald-500">{formatCurrency(paidAmount)}</span>
              </div>
            )}
            <Separator className="my-2" />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground font-medium">Restante</span>
              <span className={`font-mono font-semibold ${realRemaining === 0 ? 'text-emerald-500' : ''}`}>
                {formatCurrency(realRemaining)}
              </span>
            </div>
            {totalPaidSoFar > total && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">{t('pdv.change')}</span>
                <span className="font-mono text-primary">{formatCurrency(totalPaidSoFar - total)}</span>
              </div>
            )}
          </div>

          {/* Payment Methods */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Forma de Pagamento</p>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethods.filter(m => m.is_active).map(method => {
                const Icon = iconMap[method.icon] || CreditCard;
                return (
                  <Button
                    key={method.id}
                    variant="outline"
                    className="h-12 justify-start"
                    onClick={() => onAddPayment(method.id)}
                    data-testid={`payment-method-${method.id}`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {method.name}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Added Payments */}
          {payments.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Pagamentos Adicionados</p>
              {payments.map((payment, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-sm flex-1 truncate">
                    {payment.payment_method_name}
                    {payment.payer_name && (
                      <span className="text-muted-foreground text-xs ml-1">
                        ({payment.payer_name})
                      </span>
                    )}
                  </span>
                  <CurrencyInput
                    value={payment.amount === '' || payment.amount === undefined ? null : payment.amount}
                    onValueChange={(val) => onUpdatePayment(idx, val ?? '')}
                    className="w-32"
                    data-testid={`payment-amount-${idx}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => onRemovePayment(idx)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Partial Payment Form */}
          {showPartialForm && (
            <>
              <Separator />
              <div className="space-y-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Pagamento Parcial
                </p>
                <p className="text-xs text-muted-foreground">
                  O cliente pagará uma parte agora e o restante depois. A comanda permanecerá aberta.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Valor a pagar</Label>
                    <CurrencyInput
                      value={partialAmount ? parseFloat(partialAmount) : null}
                      onValueChange={(val) => setPartialAmount(val?.toString() ?? '')}
                      data-testid="partial-amount-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nome do pagante</Label>
                    <Input
                      type="text"
                      placeholder="Ex: João"
                      value={payerName}
                      onChange={(e) => setPayerName(e.target.value)}
                      data-testid="payer-name-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowPartialForm(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handlePartialPayment}
                    disabled={!partialAmount || parseFloat(partialAmount) <= 0}
                    className="flex-1"
                    data-testid="confirm-partial-btn"
                  >
                    Confirmar
                  </Button>
                </div>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter className="flex-col sm:flex-row gap-2">
          {/* Close and Continue Button */}
          <Button variant="outline" onClick={handleCloseAndContinue}>
            <X className="h-4 w-4 mr-2" />
            Fechar e Continuar
          </Button>
          
          {/* Partial Payment Button */}
          {canPartialPay && !showPartialForm && (
            <Button 
              variant="secondary"
              onClick={() => setShowPartialForm(true)}
              data-testid="partial-payment-btn"
            >
              <Clock className="h-4 w-4 mr-2" />
              Pagar Parcial
            </Button>
          )}
          
          {/* Finalize Button */}
          <Button 
            onClick={onFinalize} 
            disabled={!canFinalize} 
            data-testid="finalize-sale-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            {t('pdv.finishSale')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default PaymentModal;
